import { User, UserGeneralNotifications } from './user';
import { OrgExternalGeneralNotifications, OrgGeneralNotifications } from './organization';

export type Notification = {
    _id: string;
    type: NotificationType;
    messageData: NotificationMessageData;
    createdAt: number;
    isRead: boolean;
    isDone?: boolean;
    doneAt?: number;
    doneBy?: User;
    resolution?: NotificationResolution;
    originator?: User | null;
    // DD-3501: lab user responsible for the order behind the notification (creator of a
    // lab-created order, otherwise the technician who last accepted it); null when unknown
    responsibleLabUser?: User | null;
};

export type NotificationResolution = {
    comment: string;
    updatedAt: number;
    updatedBy?: User;
};

export type NotificationMessageData = {
    messageInfo: string; // Print friendly message (eg. orderId, patient name etc.)
    subjectId?: string; // Database id for related entity
    subjectType?: string; // Type (collection) related entity
    extraInfo?: string;
};

export enum NotificationCategory {
    CUSTOMER = 'CUSTOMER',
    SUPPLIER = 'SUPPLIER',
    // DD-3697: not about an order — price requests ("System notifications" tab for labs).
    SYSTEM = 'SYSTEM',
}

export enum NotificationType {
    ORDER_CREATED = 'ORDER_CREATED',
    ORDER_MODIFIED = 'ORDER_MODIFIED',
    INTERNAL_ORDER_MODIFIED = 'INTERNAL_ORDER_MODIFIED',
    ORDER_RESENT = 'ORDER_RESENT',
    NEW_INTERNAL_CHAT_MESSAGE = 'NEW_INTERNAL_CHAT_MESSAGE',
    NEW_CHAT_MESSAGE = 'NEW_CHAT_MESSAGE',
    EXTERNAL_TECH_ORDER_ASSIGNED = 'EXTERNAL_TECH_ORDER_ASSIGNED',
    ORDER_RATED = 'ORDER_RATED',
    ORDER_CANCELED = 'ORDER_CANCELED',
    ORDER_DECLINED = 'ORDER_DECLINED',
    NEW_COMPLETED_DRAFT_ORDER = 'NEW_COMPLETED_DRAFT_ORDER',
    ORDER_RECEIVED_BY_LAB = 'ORDER_RECEIVED_BY_LAB',
    ORDER_SENT_BY_LAB = 'ORDER_SENT_BY_LAB',
    INVOICE_BATCH_FAILED = 'INVOICE_BATCH_FAILED',
    INVOICE_BATCH_PARTIAL = 'INVOICE_BATCH_PARTIAL',
    // DD-3633: a collective invoice run finished with every invoice processed.
    INVOICE_BATCH_COMPLETED = 'INVOICE_BATCH_COMPLETED',
    // DD-3662 Marketplace supplier requests.
    SUPPLIER_REQUEST_RECEIVED = 'SUPPLIER_REQUEST_RECEIVED',
    SUPPLIER_REQUEST_APPROVED = 'SUPPLIER_REQUEST_APPROVED',
    SUPPLIER_REQUEST_DECLINED = 'SUPPLIER_REQUEST_DECLINED',
    // DD-3702: a lab or a Denthub Pro clinic asked to work with the recipient / it answered.
    CLINIC_REQUEST_RECEIVED = 'CLINIC_REQUEST_RECEIVED',
    CLINIC_REQUEST_APPROVED = 'CLINIC_REQUEST_APPROVED',
    CLINIC_REQUEST_DECLINED = 'CLINIC_REQUEST_DECLINED',
    // DD-3697 Price requests; subjectId is the request id.
    PRICE_REQUEST_RECEIVED = 'PRICE_REQUEST_RECEIVED',
    PRICE_REQUEST_RESPONDED = 'PRICE_REQUEST_RESPONDED',
    PRICE_REQUEST_APPROVED = 'PRICE_REQUEST_APPROVED',
    PRICE_REQUEST_REJECTED = 'PRICE_REQUEST_REJECTED',
    PRICE_REQUEST_CANCELLED = 'PRICE_REQUEST_CANCELLED',
    PRICE_REQUEST_MESSAGE = 'PRICE_REQUEST_MESSAGE',
    PRICE_REQUEST_UPDATED = 'PRICE_REQUEST_UPDATED',
    // DD-3726: a customer's answer to the lab's e-invoice invite, or a lost e-invoice customer.
    E_INVOICE_CUSTOMER_ACCEPTED = 'E_INVOICE_CUSTOMER_ACCEPTED',
    E_INVOICE_CUSTOMER_DECLINED = 'E_INVOICE_CUSTOMER_DECLINED',
    E_INVOICE_CUSTOMER_NOT_READY = 'E_INVOICE_CUSTOMER_NOT_READY',
}

// DD-3633: a collective invoice run closed (lab "System notifications").
export const INVOICE_BATCH_NOTIFICATION_TYPES: NotificationType[] = [
    NotificationType.INVOICE_BATCH_COMPLETED,
    NotificationType.INVOICE_BATCH_PARTIAL,
    NotificationType.INVOICE_BATCH_FAILED,
];

// DD-3697: price-request notifications are always delivered (no settings switch). Clinics get
// them per DENTIST_ADMIN; labs under "System notifications".
export const PRICE_REQUEST_NOTIFICATION_TYPES: NotificationType[] = [
    NotificationType.PRICE_REQUEST_RECEIVED,
    NotificationType.PRICE_REQUEST_RESPONDED,
    NotificationType.PRICE_REQUEST_APPROVED,
    NotificationType.PRICE_REQUEST_REJECTED,
    NotificationType.PRICE_REQUEST_CANCELLED,
    NotificationType.PRICE_REQUEST_MESSAGE,
    NotificationType.PRICE_REQUEST_UPDATED,
];

// The type filter offers only the notification types behind the audience's
// notification settings: the lab's settings (lab settings page) for lab users,
// the user's own settings (account settings) for clinic users. The mapping of
// setting -> types matches shouldNotifyOrganization / shouldNotifyUser in
// shared-denthub-code. A toggle only hides its types when explicitly disabled.

const enabledTypes = (mapping: [boolean | undefined, NotificationType[]][]): NotificationType[] => {
    return mapping.flatMap(([enabled, types]) => (enabled !== false ? types : []));
};

// Lab settings -> Notifications (organization.generalNotifications)
export const getLabNotificationTypes = (settings?: OrgGeneralNotifications): NotificationType[] => {
    const types = enabledTypes([
        [settings?.newOrder, [NotificationType.ORDER_CREATED]],
        [settings?.newMessage, [NotificationType.NEW_CHAT_MESSAGE, NotificationType.NEW_INTERNAL_CHAT_MESSAGE]],
        [settings?.orderModified, [NotificationType.ORDER_MODIFIED, NotificationType.INTERNAL_ORDER_MODIFIED]],
        [settings?.orderResend, [NotificationType.ORDER_RESENT]],
        [settings?.orderCancelled, [NotificationType.ORDER_CANCELED]],
        [settings?.orderRated, [NotificationType.ORDER_RATED]],
        // DD-3492: orders the lab placed with a partner lab (the customer side). No cancel
        // here: only the customer cancels, the supplier declines (PO 2026-10-13).
        [settings?.orderSentByLab, [NotificationType.ORDER_SENT_BY_LAB]],
        [settings?.orderDeclined, [NotificationType.ORDER_DECLINED]],
        [settings?.placedOrderNewMessage, [NotificationType.NEW_CHAT_MESSAGE]],
        // DD-3662: supplier requests reach the lab whatever its settings (no switch yet).
        [
            true,
            [
                NotificationType.SUPPLIER_REQUEST_RECEIVED,
                NotificationType.SUPPLIER_REQUEST_APPROVED,
                NotificationType.SUPPLIER_REQUEST_DECLINED,
                // DD-3702: requests between the lab and Denthub Pro clinics, likewise.
                NotificationType.CLINIC_REQUEST_RECEIVED,
                NotificationType.CLINIC_REQUEST_APPROVED,
                NotificationType.CLINIC_REQUEST_DECLINED,
                // DD-3726: the lab has to act on a customer's e-invoice answer.
                NotificationType.E_INVOICE_CUSTOMER_ACCEPTED,
                NotificationType.E_INVOICE_CUSTOMER_DECLINED,
                NotificationType.E_INVOICE_CUSTOMER_NOT_READY,
            ],
        ],
        // DD-3633: the lab's "System notifications" switches.
        [settings?.priceRequests ?? true, PRICE_REQUEST_NOTIFICATION_TYPES],
        [settings?.invoiceBatches ?? true, INVOICE_BATCH_NOTIFICATION_TYPES],
        // "Accepted" is off unless the lab turned it on.
        [settings?.orderReceivedByLab === true, [NotificationType.ORDER_RECEIVED_BY_LAB]],
    ]);
    return Array.from(new Set(types));
};

// Lab settings -> Notifications for external users (organization.externalGeneralNotifications)
export const getExternalTechnicianNotificationTypes = (
    settings?: OrgExternalGeneralNotifications,
): NotificationType[] => {
    return enabledTypes([
        [settings?.newInternalMessage, [NotificationType.NEW_INTERNAL_CHAT_MESSAGE]],
        [settings?.internalOrderModified, [NotificationType.INTERNAL_ORDER_MODIFIED]],
        [settings?.orderModified, [NotificationType.ORDER_MODIFIED]],
        [settings?.orderDeclined, [NotificationType.ORDER_DECLINED]],
        [settings?.assignExtTech, [NotificationType.EXTERNAL_TECH_ORDER_ASSIGNED]],
    ]);
};

// User settings -> Notification settings (user.generalNotifications, clinic users)
export const getClinicNotificationTypes = (settings?: UserGeneralNotifications): NotificationType[] => {
    return enabledTypes([
        [settings?.newMessage, [NotificationType.NEW_CHAT_MESSAGE]],
        [settings?.orderReceivedByLab, [NotificationType.ORDER_RECEIVED_BY_LAB]],
        [settings?.orderDeclinedByLab, [NotificationType.ORDER_DECLINED]],
        [settings?.orderSentByLab, [NotificationType.ORDER_SENT_BY_LAB]],
        // DD-3702: lab requests reach the clinic's admins whatever their settings (no switch yet).
        [
            true,
            [
                NotificationType.CLINIC_REQUEST_RECEIVED,
                NotificationType.CLINIC_REQUEST_APPROVED,
                NotificationType.CLINIC_REQUEST_DECLINED,
            ],
        ],
        [true, PRICE_REQUEST_NOTIFICATION_TYPES],
    ]);
};
